import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Tables from "layouts/tables";
import { useState } from "react";
import {
  useGetReportIssuesQuery,
  useUpdateReportStatusMutation,
} from "slices/common/reportIssueSlice";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Typography,
} from "@mui/material";

const STATUS_COLORS = {
  active: "warning",
  resolved: "success",
  removed: "error",
};

function ReportIssues() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("");

  const { data, isLoading, refetch } = useGetReportIssuesQuery({ page, limit, search });
  const [updateStatus] = useUpdateReportStatusMutation();

  const reports = data?.data?.data || [];
  const totalPages = data?.data?.pagination?.totalPages || 0;

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const changePage = (newPage, newLimit) => {
    setPage(newPage + 1);
    setLimit(newLimit);
  };

  const openModal = (row) => {
    setSelected(row);
    setNewStatus(row.status);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setNewStatus("");
  };

  const handleSubmit = async () => {
    try {
      await updateStatus({ id: selected._id, status: newStatus }).unwrap();
      refetch();
      closeModal();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const columns = [
    {
      display: "Reported By",
      key: "reportedBy",
      type: "display",
      render: (row) => row.reportedBy?.name || "—",
    },
    {
      display: "Type",
      key: "reporterType",
      type: "display",
      render: (row) => (
        <Chip
          label={row.reporterType || "—"}
          size="small"
          color={row.reporterType === "beautician" ? "secondary" : "primary"}
          sx={{ textTransform: "capitalize", fontWeight: 600, fontSize: 11 }}
        />
      ),
    },
    {
      display: "Title",
      key: "title",
      type: "display",
      render: (row) => row.title || "—",
    },
    {
      display: "Description",
      key: "description",
      type: "display",
      render: (row) => (
        <span style={{ maxWidth: 220, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {row.description || "—"}
        </span>
      ),
    },
    {
      display: "Status",
      key: "status",
      type: "display",
      render: (row) => (
        <Chip
          label={row.status}
          size="small"
          color={STATUS_COLORS[row.status] || "default"}
          sx={{ textTransform: "capitalize", fontWeight: 600, fontSize: 11 }}
        />
      ),
    },
    {
      display: "Date",
      key: "createdAt",
      type: "display",
      render: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleDateString() : "—",
    },
    {
      display: "Action",
      key: "_id",
      type: "display",
      render: (row) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => openModal(row)}
          style={{ background: "#338ced", color: "#fff", textTransform: "none", fontSize: 12 }}
        >
          Update Status
        </Button>
      ),
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Tables
        title="Report Issues"
        handleSearch={handleSearchChange}
        totalPages={totalPages}
        data={reports}
        columns={columns}
        changePage={changePage}
        currentPage={page}
        rowsPerPage={limit}
      />
      <Footer />

      <Dialog open={modalOpen} onClose={closeModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Update Report Status
          {selected && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {selected.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="removed">Removed</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeModal} variant="outlined" color="inherit" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!newStatus || newStatus === selected?.status}
            style={{ background: "#338ced", color: "#fff", textTransform: "none" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ReportIssues;
